import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Database,
  FlaskConical,
  RotateCcw,
  ShieldCheck,
  Sparkles,
  Upload,
  Users,
} from "lucide-react";
import { toast } from "sonner";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { AdminSection } from "@/components/admin/AdminSection";
import { DataTable, KpiCard, num, type DataColumn } from "@/components/admin/analytics/AnalyticsKit";
import { RefreshBar } from "@/components/admin/RefreshBar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  getSystemOverview,
  loadDemoData,
  resetDemoData,
  updateSystemSettings,
} from "@/lib/admin/system/system.functions";
import {
  DEMO_SWITCH_ORDER,
  type DemoEntity,
  type SystemOverview,
} from "@/lib/admin/system/system.types";

export const Route = createFileRoute("/_admin/admin/demo-data")({
  head: () => ({
    meta: [
      { title: "Demo Data Manager — Admin" },
      {
        name: "description",
        content:
          "Switch between Demo and Production mode, show or hide each demo dataset, and reload demo data from a template.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: DemoDataManagerPage,
});

function DemoDataManagerPage() {
  const qc = useQueryClient();
  const fetchOverview = useServerFn(getSystemOverview);
  const saveSettings = useServerFn(updateSystemSettings);
  const runLoad = useServerFn(loadDemoData);
  const runReset = useServerFn(resetDemoData);

  const [template, setTemplate] = useState<string | null>(null);
  const [confirmLoad, setConfirmLoad] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "system-overview"],
    queryFn: () => fetchOverview(),
  });

  const settings = data?.settings;
  const activeTemplate = template ?? settings?.active_template ?? "healthy_kitchen";
  const production = settings?.system_mode === "production";
  const superAdmin = settings?.is_super_admin === true;

  const entityByKey = useMemo(() => {
    const m = new Map<string, DemoEntity>();
    for (const e of data?.entities ?? []) m.set(e.key, e);
    return m;
  }, [data]);

  const totals = useMemo(() => {
    let demo = 0;
    let real = 0;
    for (const e of data?.entities ?? []) {
      demo += e.demo_count;
      real += e.real_count;
    }
    const on = DEMO_SWITCH_ORDER.filter((k) => settings?.switches?.[k] !== false).length;
    return { demo, real, on };
  }, [data, settings]);

  const mutate = useMutation({
    mutationFn: (input: Parameters<typeof saveSettings>[0]["data"]) =>
      saveSettings({ data: input }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin", "system-overview"] });
    },
    onError: () => toast.error("Only a Super Admin can change these settings."),
  });

  const loadMutation = useMutation({
    mutationFn: () => runLoad({ data: { template: activeTemplate } }),
    onSuccess: (res) => {
      toast.success(`Demo data refreshed — ${num(res.total)} records tagged.`);
      void qc.invalidateQueries({ queryKey: ["admin", "system-overview"] });
    },
    onError: () => toast.error("Demo data could not be loaded."),
  });

  const resetMutation = useMutation({
    mutationFn: () => runReset({ data: {} }),
    onSuccess: () => {
      toast.success("Demo data reset to its original state.");
      void qc.invalidateQueries({ queryKey: ["admin", "system-overview"] });
    },
    onError: () => toast.error("Demo data could not be reset."),
  });

  const setMode = (mode: "demo" | "production") =>
    mutate.mutate({ system_mode: mode });

  const toggleSwitch = (key: string, next: boolean) =>
    mutate.mutate({ switches: { ...(settings?.switches ?? {}), [key]: next } });

  const runColumns: Array<DataColumn<SystemOverview["runs"][number]>> = [
    {
      key: "created_at",
      header: "When",
      render: (r) => new Date(r.created_at).toLocaleString("en-IN"),
      sortValue: (r) => r.created_at,
      exportValue: (r) => r.created_at,
    },
    {
      key: "action",
      header: "Action",
      render: (r) => <Badge variant="secondary">{r.action}</Badge>,
      exportValue: (r) => r.action,
    },
    {
      key: "template",
      header: "Template",
      render: (r) => r.template ?? "—",
      exportValue: (r) => r.template ?? "",
    },
    {
      key: "total",
      header: "Records",
      align: "right",
      render: (r) => num(Number(r.counts["_total"] ?? 0)),
      sortValue: (r) => Number(r.counts["_total"] ?? 0),
      exportValue: (r) => Number(r.counts["_total"] ?? 0),
    },
    {
      key: "message",
      header: "Notes",
      render: (r) => r.message ?? "—",
      exportValue: (r) => r.message ?? "",
    },
  ];

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="Demo Data Manager"
        description="Run the application on rich demonstration data, then switch to Production Mode in one click. Nothing is ever deleted here."
      />

      <RefreshBar targets={[{ key: "system-overview", label: "Demo data" }]} busy={isLoading} />

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard
          label="System mode"
          value={production ? "Production" : "Demo"}
          tone={production ? "success" : "info"}
          icon={production ? ShieldCheck : FlaskConical}
          hint={production ? "Only real records are visible" : "Demonstration data is visible"}
          loading={isLoading}
        />
        <KpiCard
          label="Demo records"
          value={num(totals.demo)}
          tone="purple"
          icon={Database}
          hint="Tagged is_demo = true"
          loading={isLoading}
        />
        <KpiCard
          label="Real records"
          value={num(totals.real)}
          tone="teal"
          icon={Users}
          hint="Created by the client"
          loading={isLoading}
        />
        <KpiCard
          label="Datasets visible"
          value={`${production ? 0 : totals.on} / ${DEMO_SWITCH_ORDER.length}`}
          tone="warn"
          icon={Sparkles}
          hint="Demo switches currently ON"
          loading={isLoading}
        />
      </div>

      <AdminSection
        number={1}
        icon={FlaskConical}
        title="System Mode"
        description="Demo Mode is used for demonstrations, testing and staff training. Production Mode shows only real customer data."
        customised={production}
      >
        <div className="grid gap-3 md:grid-cols-2">
          {(
            [
              {
                key: "demo" as const,
                title: "Demo Mode",
                copy: "Used for demonstrations, testing and staff training. Demo and real records are shown side by side.",
              },
              {
                key: "production" as const,
                title: "Production Mode",
                copy: "Only real customer data is visible. All demo records are hidden — nothing is deleted.",
              },
            ]
          ).map((opt) => {
            const active = (settings?.system_mode ?? "demo") === opt.key;
            return (
              <button
                key={opt.key}
                type="button"
                disabled={!superAdmin}
                onClick={() => setMode(opt.key)}
                className={`rounded-lg border p-4 text-left transition-colors disabled:opacity-60 ${
                  active ? "border-primary bg-muted" : "hover:bg-muted/50"
                }`}
              >
                <span className="flex items-center gap-2">
                  <span
                    className={`h-3.5 w-3.5 rounded-full border ${active ? "border-primary bg-primary" : "border-muted-foreground"}`}
                    aria-hidden
                  />
                  <span className="text-sm font-semibold text-foreground">{opt.title}</span>
                </span>
                <span className="mt-1.5 block text-xs text-muted-foreground">{opt.copy}</span>
              </button>
            );
          })}
        </div>
        {!superAdmin ? (
          <p className="mt-3 text-xs text-muted-foreground">
            Only a Super Admin can change the system mode.
          </p>
        ) : null}
      </AdminSection>

      <AdminSection
        number={2}
        icon={Database}
        title="Demo Data Switches"
        description="Show or hide each demonstration dataset independently. Turning a switch OFF hides the records — it never deletes anything."
        actions={
          production ? <Badge variant="secondary">Hidden in Production Mode</Badge> : null
        }
      >
        <div className="grid gap-x-6 gap-y-1 md:grid-cols-2 xl:grid-cols-3">
          {DEMO_SWITCH_ORDER.map((key) => {
            const entity = entityByKey.get(key);
            const on = !production && settings?.switches?.[key] !== false;
            return (
              <div
                key={key}
                className="flex items-center justify-between gap-3 border-b py-2 last:border-b-0"
              >
                <div className="min-w-0">
                  <Label htmlFor={`sw-${key}`} className="text-sm font-medium">
                    {entity?.label ?? key}
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    {num(entity?.demo_count ?? 0)} demo · {num(entity?.real_count ?? 0)} real
                  </p>
                </div>
                <Switch
                  id={`sw-${key}`}
                  checked={on}
                  disabled={production || !superAdmin}
                  onCheckedChange={(v) => toggleSwitch(key, v)}
                />
              </div>
            );
          })}
        </div>
      </AdminSection>

      <AdminSection
        number={3}
        icon={Sparkles}
        title="Demo Templates"
        description="Pick the business type to demonstrate. Each template loads the datasets relevant to that operation."
      >
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {(data?.templates ?? []).map((t) => {
            const active = activeTemplate === t.key;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setTemplate(t.key)}
                className={`rounded-lg border p-4 text-left transition-colors ${
                  active ? "border-primary bg-muted" : "hover:bg-muted/50"
                }`}
              >
                <span className="flex items-center justify-between gap-2">
                  <span className="text-sm font-semibold text-foreground">{t.label}</span>
                  {active ? <Badge variant="secondary">Selected</Badge> : null}
                </span>
                <span className="mt-1 block text-xs text-muted-foreground">{t.description}</span>
                <span className="mt-2 block text-[11px] text-muted-foreground">
                  {t.entities.length} datasets
                </span>
              </button>
            );
          })}
        </div>

        <div className="mt-5 flex flex-wrap gap-3">
          <Button
            size="lg"
            disabled={!superAdmin || loadMutation.isPending}
            onClick={() => setConfirmLoad(true)}
            className="gap-2"
          >
            <Upload className="h-4 w-4" aria-hidden />
            {loadMutation.isPending ? "Loading demo data…" : "Load Demo Data"}
          </Button>
          <Button
            size="lg"
            variant="outline"
            disabled={!superAdmin || resetMutation.isPending}
            onClick={() => setConfirmReset(true)}
            className="gap-2"
          >
            <RotateCcw className="h-4 w-4" aria-hidden />
            Reset Demo Data
          </Button>
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          Loading is idempotent — existing demo records are updated in place, so duplicates can
          never be created.
        </p>
      </AdminSection>

      <AdminSection
        number={4}
        icon={ShieldCheck}
        title="Activity Log"
        description="Every load, reset, cleanup, backup and configuration import is recorded here."
        defaultOpen={false}
      >
        <DataTable
          rows={data?.runs ?? []}
          columns={runColumns}
          loading={isLoading}
          exportName="system-administration-activity"
          searchOn={(r) => `${r.action} ${r.template ?? ""} ${r.message ?? ""}`}
        />
      </AdminSection>

      <AlertDialog open={confirmLoad} onOpenChange={setConfirmLoad}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reload demonstration data?</AlertDialogTitle>
            <AlertDialogDescription>
              This will reload all demonstration records. Existing Production Data will NOT be
              modified.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => loadMutation.mutate()}>
              Load Demo Data
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmReset} onOpenChange={setConfirmReset}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset demo data?</AlertDialogTitle>
            <AlertDialogDescription>
              Demo kitchen production, dispatch, deliveries, tracking and stock levels return to
              their original state. Real customer data is untouched.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => resetMutation.mutate()}>
              Reset Demo Data
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
